import React, { useEffect, useState } from 'react';
import { Table, message } from 'antd';
import { getReportApiByKey } from '@app/api/reports.api';

interface CategoryData {
  id: string | number;
  code: string;
  name: string;
  importTotal?: number;
  exportTotal?: number;
}

interface DepartmentData {
  id: string | number;
  code: string;
  name: string;
  importTotal?: number;
  exportTotal?: number;
  categories?: CategoryData[];
}

interface Props {
  reportKey: string;
  filters?: Record<string, any>;
}

const ImportExportComparisonLayout: React.FC<Props> = ({ reportKey, filters }) => {
  const [data, setData] = useState<DepartmentData[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalImport, setTotalImport] = useState<number>(0);
  const [totalExport, setTotalExport] = useState<number>(0);

  const isEarningsReport = reportKey === 'imp-exp-earnings-comparison';
  const isCountReport = reportKey === 'imp-exp-counts-comparison';

  const columns: any[] = [
    {
      title: 'Department / Category',
      dataIndex: 'name',
      key: 'name',
    },
  ];

  if (isEarningsReport) {
    columns.push(
      {
        title: 'Import Earnings (LKR)',
        dataIndex: 'importTotal',
        key: 'importTotal',
        render: (value: number | undefined) =>
          value !== undefined ? value.toLocaleString('en-LK', { minimumFractionDigits: 2 }) : '-',
      },
      {
        title: 'Export Earnings (LKR)',
        dataIndex: 'exportTotal',
        key: 'exportTotal',
        render: (value: number | undefined) =>
          value !== undefined ? value.toLocaleString('en-LK', { minimumFractionDigits: 2 }) : '-',
      },
    );
  } else if (isCountReport) {
    columns.push(
      {
        title: 'Import Count',
        dataIndex: 'importTotal',
        key: 'importTotal',
        render: (value: number | undefined) => (value !== undefined ? value.toLocaleString('en-LK') : '-'),
      },
      {
        title: 'Export Count',
        dataIndex: 'exportTotal',
        key: 'exportTotal',
        render: (value: number | undefined) => (value !== undefined ? value.toLocaleString('en-LK') : '-'),
      },
    );
  }

  useEffect(() => {
    const fetchReport = async () => {
      if (!reportKey || !filters) return;

      const reportApi = getReportApiByKey(reportKey);
      if (!reportApi) {
        message.error('Invalid report key.');
        return;
      }

      setLoading(true);
      try {
        const response = await reportApi.generateReport(filters);
        const reportData = response?.report || {};

        // Set overall totals
        setTotalImport(reportData.import_total || 0);
        setTotalExport(reportData.export_total || 0);

        // Transform the nested report structure: Department → Categories
        const departments: DepartmentData[] = Object.entries(reportData.departments || {}).map(
          ([deptId, dept]: [string, any]) => ({
            id: deptId,
            code: dept.department_code,
            name: `${dept.department_code} - ${dept.department_name}`,
            importTotal: dept.import_total,
            exportTotal: dept.export_total,
            categories: Object.entries(dept.categories || {}).map(([catId, category]: [string, any]) => ({
              id: catId,
              code: category.product_category_code,
              name: `${category.product_category_code} - ${category.product_category_name}`,
              importTotal: category.import_total,
              exportTotal: category.export_total,
            })),
          }),
        );

        setData(departments);
      } catch (error) {
        console.error('Error fetching report data:', error);
        message.error('Failed to load report data.');
      } finally {
        setLoading(false);
      }
    };

    fetchReport();
  }, [reportKey, filters]);

  // Transform data for expandable rows
  const tableData = data.map((dept) => ({
    key: `dept-${dept.id}`,
    name: dept.name,
    importTotal: dept.importTotal,
    exportTotal: dept.exportTotal,
    children: dept.categories?.map((category) => ({
      key: `dept-${dept.id}-cat-${category.id}`,
      name: `  ${category.name}`,
      importTotal: category.importTotal,
      exportTotal: category.exportTotal,
    })),
  }));

  const getTableTitle = () => {
    if (isCountReport) {
      return 'Import/Export Counts Comparison';
    }
    return 'Import/Export Earnings Comparison';
  };

  return (
    <Table
      title={() => <div style={{ textAlign: 'center', fontWeight: 700 }}>{getTableTitle()}</div>}
      columns={columns}
      dataSource={tableData}
      pagination={false}
      expandable={{ defaultExpandAllRows: true }}
      bordered
      loading={loading}
      rowKey="key"
      summary={() => (
        <Table.Summary fixed>
          <Table.Summary.Row style={{ fontWeight: 'bold' }}>
            <Table.Summary.Cell index={0}>Total</Table.Summary.Cell>
            <Table.Summary.Cell index={1} align="right">
              {isEarningsReport
                ? totalImport.toLocaleString('en-LK', { minimumFractionDigits: 2 })
                : totalImport.toLocaleString('en-LK')}
            </Table.Summary.Cell>
            <Table.Summary.Cell index={2} align="right">
              {isEarningsReport
                ? totalExport.toLocaleString('en-LK', { minimumFractionDigits: 2 })
                : totalExport.toLocaleString('en-LK')}
            </Table.Summary.Cell>
          </Table.Summary.Row>
        </Table.Summary>
      )}
    />
  );
};

export default ImportExportComparisonLayout;
